import { vehicleSelectionHierarchy, VehicleSelectionConstants } from '../constants';
import { VehicleSelectionHierarchyLabel, VehicleSelectionMethod } from '../enums';
import { VehicleSelectionService } from './VehicleSelectionService';
/**
 * Contains business logic applicable to handling vehicle info decode operations.
 */
export class VehicleInfoOperations {
    /**
     * Handles a flyout clicked operation with the given data. Returns the updated vehicle info data.
     * @param vehicleInfo The source vehicle info data
     * @param vehicleSelectionHierarchyLabel The hierarchy label corresponding to the clicked flyout.
     * @param vehicleSelectionMethod The currently employed vehicle selection method.
     * @param vehicleInfoEntry The full vehicle info entry object corresponding to the clicked item.
     */
    static handleFlyoutClicked(vehicleInfo, vehicleSelectionHierarchyLabel, vehicleSelectionMethod, vehicleInfoEntry, vehicleSelectionService = new VehicleSelectionService()) {
        const uri = vehicleInfoEntry ? vehicleInfoEntry.Next : '';
        let vehicleSelectionMethodInferred = vehicleSelectionMethod;
        if (vehicleInfoEntry && vehicleSelectionHierarchyLabel === VehicleSelectionHierarchyLabel.Types) {
            vehicleSelectionMethodInferred =
                VehicleSelectionConstants.genericDecodeTypeIds.indexOf(vehicleInfoEntry.Id) !== -1
                    ? VehicleSelectionMethod.GenericVehicle
                    : VehicleSelectionMethod.Assisted;
        }
        vehicleInfo.VehicleSelectionMethod = vehicleSelectionMethodInferred;
        return vehicleSelectionService.decodeWithUri(vehicleInfo, uri, vehicleSelectionMethodInferred === VehicleSelectionMethod.VinDecode);
    }
    /**
     * Handles vin decode operations using the given vehicle info object. Returns an updated vehicle info object or null in the event vin decode doesn't apply.
     * @param vehicleInfoData The source vehicle info object. Object must specify a vin decode selection method to be used.
     * @param vin The vin to decode with.
     * @param vid The vid to decode with.
     */
    static handleVinDecode(vehicleInfoData, vin, vid, hasSelectionMethodChanged, vehicleSelectionService = new VehicleSelectionService()) {
        if (vehicleInfoData.VehicleSelectionMethod === VehicleSelectionMethod.VinDecode || hasSelectionMethodChanged) {
            return vehicleSelectionService.decodeWithVin(vehicleInfoData, vin, vid);
        }
        return Promise.resolve(null);
    }
    /**
     * Handles vehicle resolve operations using the given vehicle info object. Returns an updated vehicle info object or null in the event vin decode doesn't apply.
     * @param vehicleInfoData The source vehicle info object. Object must specify a vin decode selection method to be used.
     * @param vin The vin to decode with.
     * @param vid The vid to decode with.
     */
    static handleVehicleResolve(vehicleInfoData, vin, vid, 
    /*istanbul ignore next*/
    vehicleSelectionService = new VehicleSelectionService()) {
        return vehicleSelectionService.decodeWithVin(vehicleInfoData, vin, vid);
    }
    /**
     * Handles load generic vehicle operations using the given vehicle info object. Returns an updated vehicle info object or null in the event.
     * @param vehicleInfoData The source vehicle info object. Object must specify a generic vehicle load selection method to be used.
     * @param model The model to load with.
     * @param year The year to load with.
     * @param typeId The type ID to load with.
     */
    static handleGenericVehicle(vehicleInfoData, model, year, typeId, 
    /*istanbul ignore next*/
    vehicleSelectionService = new VehicleSelectionService()) {
        if (vehicleInfoData.VehicleSelectionMethod === VehicleSelectionMethod.GenericVehicle) {
            return vehicleSelectionService.decodeGeneric(vehicleInfoData, model, year, typeId);
        }
        return Promise.resolve(null);
    }
    /**
     * Handles updating a vehicle info state object's selected info entries given the corresponding hierarchy label and vehicle info entry.
     * @param state The current vehicle info state.
     * @param vehicleSelectionHierarchyLabel The selected vehicle selection hierarchy label.
     * @param vehicleInfoEntry The selected vehicle info entry.
     */
    static updateSelectedInfoEntries(state, vehicleSelectionHierarchyLabel, vehicleInfoEntry) {
        state.set(vehicleSelectionHierarchyLabel, vehicleInfoEntry);
        VehicleInfoOperations.resetSuccessiveProperties(state, vehicleSelectionHierarchyLabel);
        return state;
    }
    /**
     * Checks to see if the provided data is a partial decode or not.
     * @param vehicleInfoData The source vehicle info object.
     */
    static isPartialDecode(vehicleInfoData) {
        const hasValues = (collection) => {
            return collection && collection.length > 1;
        };
        if (hasValues(vehicleInfoData.Years) ||
            hasValues(vehicleInfoData.Makes) ||
            hasValues(vehicleInfoData.Models) ||
            hasValues(vehicleInfoData.SubModels) ||
            hasValues(vehicleInfoData.Transmissions) ||
            hasValues(vehicleInfoData.DriveTrains) ||
            hasValues(vehicleInfoData.Engines) ||
            hasValues(vehicleInfoData.BodyStyles)) {
            return true;
        }
        return false;
    }
    /**
     * Checks for missing vehicle data with the given vehicle info data object
     * @param vehicleInfoData The source vehicle info object.
     */
    static hasMissingVehicleData(vehicleInfoData) {
        return (!vehicleInfoData.Years ||
            vehicleInfoData.Years.length === 0 ||
            !vehicleInfoData.Makes ||
            vehicleInfoData.Makes.length === 0 ||
            !vehicleInfoData.Models ||
            vehicleInfoData.Models.length === 0 ||
            !vehicleInfoData.SubModels ||
            vehicleInfoData.SubModels.length === 0 ||
            !vehicleInfoData.Transmissions ||
            vehicleInfoData.Transmissions.length === 0 ||
            !vehicleInfoData.DriveTrains ||
            vehicleInfoData.DriveTrains.length === 0 ||
            !vehicleInfoData.Engines ||
            vehicleInfoData.Engines.length === 0 ||
            !vehicleInfoData.BodyStyles ||
            vehicleInfoData.BodyStyles.length === 0);
    }
    static resetSuccessiveProperties(state, vehicleSelectionHierarchyLabel) {
        const startIndex = vehicleSelectionHierarchy.indexOf(vehicleSelectionHierarchyLabel);
        let currentIndex = startIndex + 1;
        let currentItem = vehicleSelectionHierarchy[currentIndex];
        while (currentItem) {
            state.delete(currentItem);
            currentItem = vehicleSelectionHierarchy[++currentIndex];
        }
    }
    static isInvalidDecodedVehicle(state) {
        const genericSelection = state.VehicleSelectionMethod &&
            state.VehicleSelectionMethod.toUpperCase() === VehicleSelectionMethod.GenericVehicle.toUpperCase();
        if (!genericSelection &&
            state.Vin &&
            VehicleInfoOperations.hasMissingVehicleData(state) &&
            !VehicleInfoOperations.isPartialDecode(state)) {
            return true;
        }
        return false;
    }
    static hydrateFlyoutClickedErroredResponse(vehicleSelectionHierarchyLabel, vehicleInfo) {
        let hydratedVehicleInfo = vehicleInfo;
        const startIndex = vehicleSelectionHierarchy.indexOf(vehicleSelectionHierarchyLabel);
        let currentIndex = startIndex + 1;
        let currentItem = vehicleSelectionHierarchy[currentIndex];
        while (currentItem) {
            hydratedVehicleInfo[currentItem] = [];
            currentItem = vehicleSelectionHierarchy[++currentIndex];
        }
        hydratedVehicleInfo.Vehicle = null;
        return hydratedVehicleInfo;
    }
}
//# sourceMappingURL=VehicleInfoOperations.js.map